import { useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import CustomDataGrid from "../../../components/ui/customDataGrid/customDataGrid.component";
import CustomModal from "../../../components/ui/modal/modal.component";
import PartPriceChangeForm from "../../../components/service/partPriceChangeForm.component";
import { useTranslation } from "react-i18next";
import { Edit, Inventory } from "@mui/icons-material";
import { PARTS_ENDPOINT } from "../../../consts";
import { useNavigate } from "react-router-dom";
import { useHasPermission } from "../../../contexts/auth";
import { formatMoney, formatQuantity } from "../../../utils";
import {
  containsStringFilterOperators,
  equalsNumberFilterOperators,
  equalsStringFilterOperators,
} from "../../../components/ui/customDataGrid/filterOperators";

export default function StockStatusList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canViewParts = useHasPermission("service.view_part");
  // Repricing lands on the part's orders, whoever placed them - so the full
  // permission, not the "own" one.
  const canChangeOrderedParts = useHasPermission("service.change_orderedpart");
  const [priceChangePart, setPriceChangePart] = useState<{ id: number; name: string } | null>(null);

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
      filterOperators: equalsNumberFilterOperators(),
    },
    {
      field: "name",
      headerName: t("Name"),
      filterOperators: containsStringFilterOperators(),
    },
    {
      field: "code",
      headerName: t("Code"),
      filterOperators: containsStringFilterOperators(),
    },
    {
      field: "unit",
      headerName: t("Unit"),
      filterOperators: equalsStringFilterOperators(),
    },
    {
      field: "stock_quantity",
      headerName: t("Stock Quantity"),
      type: "number",
      filterOperators: equalsNumberFilterOperators(),
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: params.row.is_below_notification_threshold ? 700 : 400 }}
            color={params.row.is_below_notification_threshold ? "error.main" : undefined}
          >
            {`${formatQuantity(params.value)} ${params.row.unit ?? ""}`.trim()}
          </Typography>
          {params.row.is_below_notification_threshold && (
            <Chip size="small" color="error" variant="outlined" label={t("Low stock")} />
          )}
        </Box>
      ),
    },
    {
      field: "notification_threshold",
      headerName: t("Notification Threshold"),
      type: "number",
      filterOperators: equalsNumberFilterOperators(),
      valueFormatter: (value) => formatQuantity(value),
    },
    {
      // Net purchase price on the order the next use is drawn from - the same
      // "Current Price" the part page shows (resolved by the backend).
      field: "current_price_net",
      headerName: t("Current Price (net)"),
      type: "number",
      filterOperators: equalsNumberFilterOperators(),
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5, height: "100%" }}>
          <Typography variant="body2">{formatMoney(params.value)}</Typography>
          {canChangeOrderedParts && params.row.has_not_used_orders && (
            // Clicks and key presses stop here, so changing the price does
            // not also open the part.
            <Box
              component="span"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <Tooltip title={t("Change price")}>
                <IconButton
                  size="small"
                  aria-label={t("Change price")}
                  onClick={() => setPriceChangePart({ id: params.row.id, name: params.row.name })}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      ),
    },
  ];

  const handleRowClick = (id: string) => {
    navigate(`/service/stock-status/${id}`);
  };

  if (!canViewParts) {
    return null;
  }

  return (
    <>
      <CustomDataGrid
        icon={<Inventory />}
        title={t("Stock Status")}
        url={PARTS_ENDPOINT}
        gridSettingsKey="STOCK_STATUS"
        columns={columns}
        refreshEventName={["partAdded", "partUpdated"]}
        onRowClick={handleRowClick}
      />
      {/* Mounted per part, so the form always loads the orders of the row it
          was opened from. */}
      {priceChangePart && (
        <CustomModal
          opened
          setOpened={(opened) => {
            if (!opened) setPriceChangePart(null);
          }}
          title={`${t("Change price")}: ${priceChangePart.name}`}
          // Fixed, so the dialog does not jump when the warning about used
          // stock comes and goes with the chosen scope.
          width={560}
          minWidth={0}
          minHeight={0}
          content={
            <PartPriceChangeForm
              partId={priceChangePart.id}
              onSuccess={() => {
                setPriceChangePart(null);
                window.dispatchEvent(new Event("partUpdated"));
              }}
            />
          }
        />
      )}
    </>
  );
}
